import PropTypes from "prop-types";
import * as React from "react";
import {
  fromDateTimeLocal,
  toDateTimeLocal,
} from "../../../domain/productComposer/edit";
import {
  Accordion,
  DateInput,
  NumberInput,
  Switch,
  Typography,
} from "../../../themeV2";
import css from "./sections.module.css";

const EVERYONE = [
  ["active", "Active", "Inactive products are hidden from your shop"],
  ["isFeatured", "Featured", "Shown first on your shop"],
  ["isHidden", "Hidden on your shop", "Still sold through direct links"],
  ["allowPersonalization", "Allow personalization", null],
  ["allowNotes", "Allow special instructions", null],
];

const ADMINS = [
  ["disableVip", "Disable VIP", null],
  ["blockCertOfAuth", "Disable certificate of authenticity", null],
  ["noShipping", "No shipping", null],
  ["isCameoExclusive", "Cameo exclusive", "Kept out of search"],
  ["convention", "Convention product", null],
];

const SettingSwitch = ({
  setting,
  label,
  description,
  settings,
  readOnly,
  onChange,
}) => (
  <Switch
    label={label}
    description={description ?? undefined}
    disabled={readOnly}
    checked={Boolean(settings[setting])}
    onCheckedChange={(value) => onChange(setting, value)}
  />
);

SettingSwitch.propTypes = {
  description: PropTypes.string,
  label: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
  setting: PropTypes.string.isRequired,
  settings: PropTypes.object.isRequired,
};

const AdvancedSection = function AdvancedSection({
  settings,
  isAdmin,
  bumpControls,
  readOnly,
  onChange,
}) {
  const switches = isAdmin ? [...EVERYONE, ...ADMINS] : EVERYONE;

  return (
    <Accordion.Root defaultValue={[]}>
      <Accordion.Item value="advanced">
        <Accordion.Header>
          <Accordion.Trigger>Advanced settings</Accordion.Trigger>
        </Accordion.Header>
        <Accordion.Panel>
          <div className={css.fields}>
            {switches.map(([setting, label, description]) => (
              <SettingSwitch
                key={setting}
                setting={setting}
                label={label}
                description={description}
                settings={settings}
                readOnly={readOnly}
                onChange={onChange}
              />
            ))}

            {isAdmin && (
              <DateInput
                type="datetime-local"
                label="Vaulted until"
                description="Hidden from sale until this time"
                disabled={readOnly}
                value={toDateTimeLocal(settings.vaultedUntil)}
                onValueChange={(value) =>
                  onChange("vaultedUntil", fromDateTimeLocal(value))
                }
              />
            )}

            {bumpControls.visible && (
              <>
                <Typography variant="title-sm" as="h4">
                  Fee
                </Typography>
                <SettingSwitch
                  setting="fixedBump"
                  label="Fix the fee"
                  description="Otherwise it is calculated from the price"
                  settings={settings}
                  readOnly={readOnly || !bumpControls.canToggle}
                  onChange={onChange}
                />
                {settings.fixedBump && (
                  <NumberInput
                    className={css.moneyInput}
                    steppers={false}
                    label="Fixed fee"
                    min={0}
                    format={{ style: "currency", currency: "USD" }}
                    disabled={readOnly || !bumpControls.canEditAmount}
                    value={(settings.productBump ?? 0) / 100}
                    onValueChange={(dollars) =>
                      onChange("productBump", Math.round((dollars ?? 0) * 100))
                    }
                  />
                )}
              </>
            )}
          </div>
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion.Root>
  );
};

AdvancedSection.propTypes = {
  isAdmin: PropTypes.bool,
  bumpControls: PropTypes.shape({
    visible: PropTypes.bool,
    canToggle: PropTypes.bool,
    canEditAmount: PropTypes.bool,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
  settings: PropTypes.object.isRequired,
};

export default AdvancedSection;
